import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Switch, FormControlLabel, TextField, Button, Alert, Chip,
} from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import SettingsIcon from '@mui/icons-material/Tune';
import { moduleAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { PageHeader } from '../../components/ui/PageHeader';
import { getPageSubtitle } from '../../constants/pageSubtitles';
import { SuperAdminDataTable } from '../../components/superadmin/SuperAdminDataTable';
import { CORE_MODULE_KEYS } from '../../config/sidebarMenu';
import { brandColors, RADIUS, brandCss } from '../../theme/appTheme';

export const ModuleSettingsPage: React.FC = () => {
  const { isTenantAdmin, hasPermission } = useAuth();
  const canEdit = isTenantAdmin || hasPermission('settings', 'edit');
  const [modules, setModules] = useState<any[]>([]);
  const [config, setConfig] = useState<any[]>([]);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    Promise.all([moduleAPI.getAll(), moduleAPI.getConfig()]).then(([m, c]) => {
      setModules(m.data);
      setConfig(c.data.modules || []);
    }).catch(() => {
      setModules(CORE_MODULE_KEYS.map((key) => ({ key, label: key, type: 'core', group: 'Operations' })));
    });
  }, []);

  const getConfig = (key: string) => config.find((c) => c.moduleKey === key) || { moduleKey: key, visible: true };

  const updateConfig = (key: string, patch: Record<string, unknown>) => {
    if (CORE_MODULE_KEYS.includes(key) && patch.visible === false) return;
    const isCustom = !CORE_MODULE_KEYS.includes(key);
    if (isCustom) return;
    const existing = getConfig(key);
    setConfig([...config.filter((c) => c.moduleKey !== key), { ...existing, moduleKey: key, ...patch }]);
  };

  const handleSave = async () => {
    try {
      setError('');
      await moduleAPI.updateConfig({ modules: config });
      setMessage('Module settings saved');
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      setError(msg || 'Save failed');
    }
  };

  const coreModules = modules.filter(
    (m) => CORE_MODULE_KEYS.includes(m.key) || m.type === 'core'
  );

  return (
    <Box>
      <PageHeader
        title="Module Control"
        subtitle={getPageSubtitle('module_settings')}
        icon={<SettingsIcon />}

      />

      {message && <Alert severity="success" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setMessage('')}>{message}</Alert>}
      {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setError('')}>{error}</Alert>}

      <SuperAdminDataTable
        rows={coreModules}
        searchPlaceholder="Search modules..."
        recordLabel="modules"
        statusOptions={[{ label: 'All', value: 'all' }]}
        getStatus={() => 'active'}
        getRowId={(m) => m.key}
        enableExport={false}
        columns={[
          {
            id: 'name',
            header: 'Module',
            searchValue: (m) => `${m.label} ${m.group} ${m.type || ''}`,
            render: (m) => {
              const isCore = CORE_MODULE_KEYS.includes(m.key) || m.type === 'core' || m.isCore;
              return (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography fontWeight={700} fontSize={13}>{m.label}</Typography>
                  <Chip icon={<LockIcon sx={{ fontSize: '14px !important' }} />} label="Core" size="small"
                    sx={{ bgcolor: brandCss.green100, color: brandCss.primaryDark, fontWeight: 600, fontSize: 11 }} />
                </Box>
              );
            },
          },
          { id: 'meta', header: 'Group', render: (m) => m.group || 'CRM' },
          {
            id: 'labelOverride',
            header: 'Display Label',
            render: (m) => {
              const c = getConfig(m.key);
              const isCore = CORE_MODULE_KEYS.includes(m.key) || m.type === 'core' || m.isCore;
              return (
                <TextField size="small" label="Display Label" value={c.labelOverride || ''} disabled={!isCore}
                  onChange={(e) => updateConfig(m.key, { labelOverride: e.target.value })} sx={{ width: 200 }} />
              );
            },
          },
          {
            id: 'visible',
            header: 'Access',
            render: (m) => {
              const isCore = CORE_MODULE_KEYS.includes(m.key) || m.type === 'core' || m.isCore;
              const c = getConfig(m.key);
              return (
                <FormControlLabel
                  control={<Switch checked={isCore ? true : c.visible === true} disabled
                    onChange={() => undefined} />}
                  label={isCore ? 'Always On' : c.visible ? 'Assigned' : 'Not assigned'}
                />
              );
            },
          },
        ]}
      />
      {isTenantAdmin && (
        <Button variant="contained" onClick={handleSave} disabled={!canEdit} sx={{ mt: 2, textTransform: 'none', fontWeight: 700, background: brandCss.gradientPrimary, borderRadius: `${RADIUS}px` }}>
          Save Changes
        </Button>
      )}
    </Box>
  );
};
